import type { MimeType } from "./constants"
import { FILE_NAME_DISPLAY_MAX_LENGTH, OPENABLE_EXTENSIONS, mimeToExtensionMap } from "./constants"

import type { SignedUrl } from "@/graphql/codegen/graphql"

export const isFileTypeAllowed = (file: File, allowedMimeTypes: string[]): boolean => {
  if (allowedMimeTypes.includes(file.type)) return true
  // Fallback: browsers may report .msg as application/octet-stream or empty string
  const ext = `.${file.name.split(".").pop()?.toLowerCase()}`
  const allowedExtensions = allowedMimeTypes.map((mime) => mimeToExtensionMap[mime as MimeType]).filter(Boolean)
  return allowedExtensions.includes(ext)
}

export const convertMimeTypesToAccept = (allowedTypes: string[]): string => {
  return allowedTypes
    .map((type) => mimeToExtensionMap[type as MimeType])
    .filter(Boolean) // Remove undefined values for unsupported MIME types
    .join(",")
}

/**
 * Handles file action based on extension type
 * - Opens PDFs, images, and text files in a new tab
 * - Downloads other file types
 *
 * @param file - SignedUrl object containing file metadata and URL
 */
export const handleFileAction = async (file: SignedUrl): Promise<void> => {
  const fileName = file?.file?.originalName as string
  const extension = fileName?.split(".").pop()?.toLowerCase()

  if (extension && OPENABLE_EXTENSIONS.has(extension)) {
    // Open in a new tab if the extension is in the openable list
    window.open(file?.url ?? "#", "_blank")
  } else {
    const fileUrl = file?.url
    try {
      const response = await fetch(fileUrl)
      if (!response.ok) throw new Error("Network response was not ok")

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = blobUrl
      link.download = file?.file?.originalName || "download"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error("Download failed:", error)
    }
  }
}

export const cleanS3FileName = (fileName: string): string => {
  // eslint-disable-next-line regexp/no-obscure-range
  const unsafeCharsRegex = /[^a-z!-_]/g
  const multipleHyphensRegex = /_+/g

  return fileName.replaceAll(unsafeCharsRegex, "_").replaceAll(multipleHyphensRegex, "_")
}

export const formatTruncatedFileName = (
  fileName: string,
  maxLength: number = FILE_NAME_DISPLAY_MAX_LENGTH
): {
  truncatedFileName: string
  isFileNameTruncated: boolean
} => {
  if (fileName.length <= maxLength) return { truncatedFileName: fileName, isFileNameTruncated: false }

  const ellipsis = "…"
  const charsToShow = maxLength - ellipsis.length
  const frontChars = Math.ceil(charsToShow / 2)
  const backChars = Math.floor(charsToShow / 2)

  return {
    truncatedFileName: `${fileName.slice(0, frontChars)}${ellipsis}${fileName.slice(fileName.length - backChars)}`,
    isFileNameTruncated: true,
  }
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}
